import {
  RECEIVE_BAD_REQUEST,
  SET_CAMERA_MODE,
  SET_USERS_DATA,
  SET_SCAN_ENDED,
  SET_SEND_REQUEST,
  SET_SEND_REQUEST_TASK,
  SET_SCAN_STARTED,
  SET_SIGNATURE_CACHE,
  SET_ATTACHMENT_PRINTED,
  SET_SUCCESS_SENT_KEYS,
  SET_SHOW_RESTORE_DIALOG,
  SET_PRIVACY_ACCEPTED,
  RECEIVE_ERROR_ON_PREPARE_FROM_TEMPLATE,
  SET_CROP_IS_HIDDEN,
  RESET_APP,
  SET_SHOW_USER_CREDENTIAL_FORM,
  SET_EXTERNAL_POPUP_WINDOW_OPEN,
  EXTERNAL_POPUP_WINDOW_STATUS,
  PREPARE_EXTERNAL_POPUP_WINDOW_OPEN,
  SET_SELECTED_LANG,
  RECEIVE_SIGNATURE_FROM_MOBILE,
  APP_CRASH,
  SET_OTP_REQUEST_ID,
  SET_OPT_VERIFY_DONE,
  SET_LANDSCAPE_MODE,
  SET_DEVICE_ID,
  SET_VIRTUAL_KEYBOARD_OPEN,
  SET_DOCUMENTS_LIST_PAGE,
  SET_ACTUAL_SCREEN_SIZE,
  SHARE_ACTIVE_DOCUMENT_STATE,
  RECEIVE_CROSS_DEVICE_DATA,
  SET_REQUEST_STEP_SUBMITTED,
  SET_ERROR_SENT_KEYS,
  RESTART_APP,
  SET_SIGNATURE_TOUCH_DATA,
  RECEIVE_CROSS_DEVICE_DOCUMENT_COMPLETED_EVENT,
  SET_CROSS_DEVICE_DOCUMENT_LOADED,
  SHOW_REJECT_DOCUMENT_DIALOG,
  HIDE_REJECT_DOCUMENT_DIALOG,
  SET_SHOW_MAIN_INFO_POPUP,
  SET_REGISTRATION_DATA,
  SET_CREATE_TEMPLATE_REQUEST_TASK,
  SET_USER_CREDENTIALS_TASK,
  RECEIVE_STANDALONE_DRAFT
} from '../actions/rootAction';

import requests from './requests';
import documents from './documents';
import esignature from './esignature';
import reduxTools from './reduxTools';
import webSocket from './webSocket';
import cronJobs from './cronJobs';
import { defineLandscapeMode } from '../../components/helpers/utils';
import { createSendRequestTask, SEND_REQUEST_STATUS } from '../sendRequestTask';
import { clearAllPendingSourcePdfs } from '../../lib/pendingSourcePdfs';

const rootReducer = (state, action) => {
  return concatReducers(state, action);
};

const concatReducers = (state, action) => {
  return [
    commonReducer,
    requests,
    documents,
    esignature,
    reduxTools,
    webSocket,
    cronJobs
  ].reduce((newState, reducer) => reducer(newState, action), state);
};

const initialState = {
  appKey: new Date().getTime(),
  recipientRequestId: null,
  requestedDocuments: null,
  forms: new Map(),
  documentsStatus: new Map(),
  scannedDocuments: new Map(),
  printedAttachments: new Set(),
  successSentKeys: new Set(),
  errorSentKeys: new Set(),
  rejectedDocuments: new Set(),
  cameraMode: null,
  oneDocumentMode: false,
  canSaveDocuments: false,
  showRestoreDialog: undefined,
  showMainInfoPopUp: false,
  templateMode: false,
  templateRequestPrepared: false,
  templateRequestPreparedError: false,
  privacyAccepted: false,
  brandingDetails: {},
  analyticsDisabled: undefined,
  pwaCanApplySignature: undefined,
  sendRequestTask: null,
  createTemplateRequestTask: null,
  userCredentialsTask: null,
  cropHidden: false,
  requestIsRestored: false,
  xcCallbackIds: {},
  signals: new Map(),
  inPersonChannel: false,
  postalChannel: false,
  customizationKey: null,
  demoRequest: false,
  registrationStatus: '',
  signatures: {},
  signmeIdentityId: '',
  atrustShortTerm: null,
  qesProvider: '',
  wsSubscriptions: new Map(),
  cronJobs: new Map(),
  recipientEmail: '',
  recipientPhone: '',
  selectedLang: window.userLang,
  externalPopUpStatus: EXTERNAL_POPUP_WINDOW_STATUS.CLOSE,
  receivedMobileSignatures: new Map(),
  signaturesTouches: new Map(),
  cachedSignature: '',
  isVirtualKeyboardOpen: false,
  isLandscape: defineLandscapeMode(),
  currentRequestPageIndex: 0,
  documentConditions: [],
  recipientConditions: [],
  activeDocumentSharedState: {},
  requestStepMovedForward: true,
  visitedDocuments: new Set(),
  collectBioDataConfirmed: new Set(),
  actualScreenSize: {},
  valueInheritance: {},
  qesValueInheritance: {},
  previewDocumentId: null,
  skipStartPage: false,
  embeddingFieldsInheritance: {},
  requestStartInfo: {},
  crossDeviceRequest: null,
  usersData: [{ userName: undefined, userEmail: undefined, userFields: [] }]
};

const commonReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_DEVICE_ID:
      return Object.assign({}, state, {
        deviceId: action.payload
      });
    case SET_LANDSCAPE_MODE:
      return Object.assign({}, state, {
        isLandscape: action.payload
      });
    case SET_ACTUAL_SCREEN_SIZE:
      return Object.assign({}, state, {
        actualScreenSize: action.payload
      });
    case SET_VIRTUAL_KEYBOARD_OPEN:
      return Object.assign({}, state, {
        isVirtualKeyboardOpen: action.payload
      });
    case APP_CRASH:
      return Object.assign({}, state, {
        appCrashed: action.payload
      });
    case RESET_APP:
      clearAllPendingSourcePdfs();
      return {
        ...initialState,
        requestStartInfo: action.newRequestStartInfo,
        appKey: new Date().getTime()
      };
    case RESTART_APP:
      return Object.assign({}, state, {
        appKey: new Date().getTime(),
        restoredAfterCrash: true
      });

    case SET_ATTACHMENT_PRINTED:
      action.as === false
        ? state.printedAttachments.delete(action.documentId)
        : state.printedAttachments.add(action.documentId);

      return Object.assign({}, state, {
        printedAttachments: new Set(state.printedAttachments)
      });

    case SET_SHOW_RESTORE_DIALOG:
      return Object.assign({}, state, {
        showRestoreDialog: action.on
      });

    case RECEIVE_STANDALONE_DRAFT: {
      const draft = action.draftState;
      if (!draft) return state;

      const mergedForms = new Map(state.forms);
      if (draft.forms instanceof Map) {
        draft.forms.forEach((form, docId) => {
          const existing = mergedForms.get(docId) || new Map();
          const merged = new Map(existing);
          if (form instanceof Map) {
            form.forEach((value, key) => merged.set(key, value));
          }
          mergedForms.set(docId, merged);
        });
      }

      return Object.assign({}, state, {
        forms: mergedForms,
        scannedDocuments:
          draft.scannedDocuments instanceof Map
            ? draft.scannedDocuments
            : state.scannedDocuments,
        documentsStatus:
          draft.documentsStatus instanceof Map
            ? draft.documentsStatus
            : state.documentsStatus,
        signatures: draft.signatures
          ? { ...state.signatures, ...draft.signatures }
          : state.signatures,
        successSentKeys:
          draft.successSentKeys instanceof Set
            ? draft.successSentKeys
            : state.successSentKeys,
        rejectedDocuments:
          draft.rejectedDocuments instanceof Set
            ? draft.rejectedDocuments
            : state.rejectedDocuments || new Set(),
        // Draft must not restore stepper position — session always resumes from page 0
        usersData: Array.isArray(draft.usersData)
          ? draft.usersData
          : state.usersData,
        requestIsRestored: true
      });
    }

    case RECEIVE_SIGNATURE_FROM_MOBILE:
      if (
        action.response.mobileSignature?.mobileSignatureId &&
        action.response.mobileSignature?.signature
      ) {
        const newInfo = new Map(state.receivedMobileSignatures);

        newInfo.set(action.response.mobileSignature.mobileSignatureId, {
          signature: action.response.mobileSignature.signature,
          biometricData: action.response.mobileSignature.biometricData
        });
        return Object.assign({}, state, {
          receivedMobileSignatures: newInfo
        });
      } else {
        return state;
      }

    case RECEIVE_CROSS_DEVICE_DOCUMENT_COMPLETED_EVENT:
      return Object.assign({}, state, {
        crossDeviceDocumentCompleted: action.response.id
      });

    case RECEIVE_CROSS_DEVICE_DATA:
      return Object.assign({}, state, {
        crossDeviceRequest: action.crossDeviceRequest,
        recipientRequestId: action.recipientRequestId,
        templateMode: action.templateMode
      });

    case RECEIVE_BAD_REQUEST:
      return Object.assign({}, state, {
        isFetching: false,
        errorMessage: action.errorMessage,
        templateRequestPreparedError: false
      });

    case RECEIVE_ERROR_ON_PREPARE_FROM_TEMPLATE:
      return Object.assign({}, state, {
        templateRequestPreparedError: 'ERROR: ' + action.errorMessage
      });

    case SET_CAMERA_MODE:
      return Object.assign({}, state, {
        cameraMode: action.as
      });

    case SET_DOCUMENTS_LIST_PAGE:
      return Object.assign({}, state, {
        requestStepMovedForward:
          action.pageIndex > state.currentRequestPageIndex,
        currentRequestPageIndex: action.pageIndex
      });

    case SET_SEND_REQUEST_TASK:
      return Object.assign({}, state, {
        sendRequestTask: action.payload
      });

    case SET_SEND_REQUEST:
      return Object.assign({}, state, {
        sendRequestTask: action.payload.active
          ? createSendRequestTask({
              status: SEND_REQUEST_STATUS.SENDING,
              fromDocView: action.payload.fromDocView,
              tillPage: action.payload.tillPage,
              noProgressDialog: action.payload.noProgressDialog,
              skipThankyou: action.payload.skipThankyou
            })
          : null
      });

    case SET_CREATE_TEMPLATE_REQUEST_TASK:
      return Object.assign({}, state, {
        createTemplateRequestTask: action.payload
      });

    case SET_USER_CREDENTIALS_TASK:
      return Object.assign({}, state, {
        userCredentialsTask: action.payload
      });

    case SET_REQUEST_STEP_SUBMITTED: {
      const completedPageIndex = state.requestPages?.findIndex(
        page => page.pageId === action.pageId
      );

      return Object.assign({}, state, {
        requestPages: state.requestPages?.map((page, index) => ({
          ...page,
          submitted: index <= completedPageIndex
        }))
      });
    }

    case SET_PRIVACY_ACCEPTED:
      return Object.assign({}, state, {
        privacyAccepted: action.as
      });

    case SET_USERS_DATA:
      return Object.assign({}, state, {
        usersData: action.data
      });

    case SET_REGISTRATION_DATA:
      return Object.assign({}, state, {
        registrationData: action.data
      });

    case SET_SUCCESS_SENT_KEYS:
      return Object.assign({}, state, {
        successSentKeys: new Set(action.keysMap)
      });

    case SET_ERROR_SENT_KEYS:
      return Object.assign({}, state, {
        errorSentKeys: new Set(action.keysMap)
      });

    case SET_SELECTED_LANG:
      window.userLang = action.payload;
      return Object.assign({}, state, {
        selectedLang: action.payload
      });

    case SET_SCAN_STARTED:
      return Object.assign({}, state, {
        scanStarted: action.startedFrom
      });

    case SET_SIGNATURE_CACHE:
      return Object.assign({}, state, {
        cachedSignature: action.data
      });

    case SET_SIGNATURE_TOUCH_DATA:
      return Object.assign({}, state, {
        signaturesTouches: new Map(state.signaturesTouches).set(action.formId, {
          id: action.signatureId,
          data: action.data
        })
      });

    case SET_CROP_IS_HIDDEN:
      return Object.assign({}, state, {
        cropHidden: action.payload
      });
    case SET_SHOW_USER_CREDENTIAL_FORM:
      return Object.assign({}, state, {
        showUserCredentialForm: action.payload
      });
    case SHOW_REJECT_DOCUMENT_DIALOG:
      return Object.assign({}, state, {
        showRejectDocumentDialog: {
          docId: action.docId
        }
      });
    case HIDE_REJECT_DOCUMENT_DIALOG:
      return Object.assign({}, state, {
        showRejectDocumentDialog: false
      });
    case SET_SHOW_MAIN_INFO_POPUP:
      return Object.assign({}, state, {
        showMainInfoPopUp: Boolean(action.payload)
      });
    case SET_SCAN_ENDED:
      return Object.assign({}, state, {
        scanEnded: true,
        submittedDate: new Date().toLocaleDateString('de-DE', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      });
    case SET_EXTERNAL_POPUP_WINDOW_OPEN:
      return Object.assign({}, state, {
        externalPopUpStatus: action.payload
      });
    case SHARE_ACTIVE_DOCUMENT_STATE:
      return Object.assign({}, state, {
        ...state,
        activeDocumentSharedState: {
          ...state.activeDocumentSharedState,
          [action.documentType]: action.sharedState
        }
      });
    case PREPARE_EXTERNAL_POPUP_WINDOW_OPEN:
      return Object.assign({}, state, {
        externalPopUpStatus: EXTERNAL_POPUP_WINDOW_STATUS.PREPARE,
        externalPopUpOptions: action.payload
      });
    case SET_OTP_REQUEST_ID:
      return {
        ...state,
        otpRequestId: action.payload
      };
    case SET_OPT_VERIFY_DONE:
      return {
        ...state,
        optVerifyDone: true
      };
    case SET_CROSS_DEVICE_DOCUMENT_LOADED:
      return {
        ...state,
        crossDeviceDocumentLoaded: true
      };
    default:
      return state;
  }
};

export default rootReducer;
